/**
 * Dictionary slot for word variants. The engine stays synchronous; the app
 * loads the word list lazily and installs it here before a Word game starts.
 */
let dictionary: Set<string> | null = null;
/** sorted-letters -> words, for AI and hint lookups. */
let anagrams: Map<string, string[]> | null = null;

export function setDictionary(words: Iterable<string>): void {
  dictionary = new Set();
  anagrams = new Map();
  for (const raw of words) {
    const w = raw.toUpperCase();
    dictionary.add(w);
    const key = [...w].sort().join('');
    const list = anagrams.get(key);
    if (list) list.push(w);
    else anagrams.set(key, [w]);
  }
}

export function hasDictionary(): boolean {
  return dictionary != null;
}

export function isWord(word: string): boolean {
  return dictionary?.has(word.toUpperCase()) ?? false;
}

export function wordsFromKey(sortedLetters: string): string[] {
  return anagrams?.get(sortedLetters) ?? [];
}

/** Letter point values (Scrabble-style). */
export const LETTER_VALUES: Record<string, number> = {
  A: 1,
  E: 1,
  I: 1,
  O: 1,
  U: 1,
  L: 1,
  N: 1,
  S: 1,
  T: 1,
  R: 1,
  D: 2,
  G: 2,
  B: 3,
  C: 3,
  M: 3,
  P: 3,
  F: 4,
  H: 4,
  V: 4,
  W: 4,
  Y: 4,
  K: 5,
  J: 8,
  X: 8,
  Q: 10,
  Z: 10,
};

export function wordValue(word: string): number {
  let v = 0;
  for (const ch of word.toUpperCase()) v += LETTER_VALUES[ch] ?? 0;
  return v;
}

/** True if `word` can be spelled from `letters` (each letter used at most once). */
export function canSpell(word: string, letters: string[]): boolean {
  const pool = new Map<string, number>();
  for (const l of letters) pool.set(l, (pool.get(l) ?? 0) + 1);
  for (const ch of word.toUpperCase()) {
    const n = pool.get(ch) ?? 0;
    if (n === 0) return false;
    pool.set(ch, n - 1);
  }
  return true;
}
